import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    PiArrowSquareOutBold,
    PiCheckBold,
    PiClipboardTextBold,
    PiCubeBold,
    PiFilePdfBold,
    PiPackageBold,
    PiTagBold,
    PiXBold,
} from 'react-icons/pi';
import { LoadingState } from '@v2/design-system';
import api from '@v2/lib/api';
import ConferenceLabelPreviewModal, {
    type LabelPreviewTarget,
} from '@v2/features/shipment-conference/ConferenceLabelPreviewModal';
import ConferenceSheetImportButton from '@v2/features/shipment-tasks/ConferenceSheetImportButton';

type PackageRow = {
    row_key: string;
    check_item_id: number;
    client: string | null;
    item_code: string | null;
    product_name: string | null;
    qty_total: number;
    package_index: number;
    package_total: number;
    box_label: string;
    frame: string | null;
    upholstery: string | null;
    stitches: string | null;
    arm: string | null;
    color_key: string;
    weight_tower_qty: number | null;
    net_weight_kg: number | null;
    box_cbm_m3: number | null;
    total_net_weight_kg: number | null;
    product_image_url: string | null;
    validated: boolean;
    preview_url?: string;
    tprt_url?: string;
    zpl_url?: string;
};

type LabelRow = {
    id: string;
    check_item_id: number;
    package_index: number;
    package_total: number;
    box_label?: string;
    case_no?: string;
    client_label: string;
    order_ref: string;
    item_code: string | null;
    product_name: string | null;
    frame_color?: string | null;
    upholstery_color?: string | null;
    stitches_color?: string | null;
    arm_color?: string | null;
    colors_summary?: string | null;
    weight_tower_qty?: number | null;
    net_weight_kg?: number | null;
    box_weight_kg?: number | null;
    box_cbm_m3?: number | null;
    conferido_por?: string | null;
    order_date_label?: string | null;
    entry_date_label?: string | null;
    scan_url?: string;
    container_number?: string | null;
    invoice_number?: string | null;
    preview_url: string;
    preview_png_url?: string;
    pdf_url?: string;
    tprt_url: string;
    zpl_url?: string;
};

type ColorOption = { key: string; label: string };

type OrderRow = {
    order_key: string;
    client_label: string;
    qty_total: number;
    packages_total: number;
    fulfillment_status: 'partial_multi_invoice' | 'single_invoice';
};

type State = {
    shipment: { id: number; order_number: string | null };
    summary: {
        has_checks: boolean;
        invoices: string[];
        containers: { id: number; container_number: string; invoice_number: string | null }[];
    };
    selection: {
        invoice_number: string | null;
        container_number: string | null;
        check_id: number | null;
    };
    package_rows: PackageRow[];
    color_options: ColorOption[];
    totals: {
        qty: number;
        packages: number;
        lines: number;
        package_rows: number;
        validated: number;
    } | null;
    orders: OrderRow[];
    labels: LabelRow[];
    label_totals: { labels: number; lines_with_packages: number } | null;
    urls: {
        check: string;
        check_v1: string;
        conference_v1: string;
        receive_cd: string;
        pi_check: string;
        pdf_container: string | null;
        pdf_orders: string | null;
        labels_bulk_pdf: string | null;
        labels_bulk_csv: string | null;
        labels_bulk_zpl?: string | null;
        labels_bulk_tprt?: string | null;
    };
};

function ValidationToggle({
    row,
    shipmentId,
    onUpdated,
}: {
    row: PackageRow;
    shipmentId: number;
    onUpdated: () => void;
}) {
    const mutation = useMutation({
        mutationFn: async (validated: boolean) => {
            await api.patch(`/shipments/${shipmentId}/conference/package-validation`, {
                check_item_id: row.check_item_id,
                package_index: row.package_index,
                validated,
            });
        },
        onSuccess: () => onUpdated(),
    });

    const busy = mutation.isPending;

    return (
        <button
            type="button"
            disabled={busy}
            onClick={() => mutation.mutate(!row.validated)}
            className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold transition ${
                row.validated
                    ? 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-300 hover:bg-emerald-200'
                    : 'bg-gray-100 text-gray-600 ring-1 ring-gray-300 hover:bg-gray-200'
            } ${busy ? 'opacity-60' : ''}`}
            title={row.validated ? 'Marcar como não conferido' : 'Marcar como conferido'}
        >
            {row.validated ? (
                <>
                    <PiCheckBold className="size-3.5" /> Validado
                </>
            ) : (
                <>
                    <PiXBold className="size-3.5" /> Pendente
                </>
            )}
        </button>
    );
}

export default function ShipmentConferencePage() {
    const { id } = useParams<{ id: string }>();
    const queryClient = useQueryClient();
    const [invoice, setInvoice] = useState<string>('');
    const [container, setContainer] = useState<string>('');
    const [colorFilter, setColorFilter] = useState<string>('');
    const [labelPreview, setLabelPreview] = useState<LabelPreviewTarget | null>(null);
    const shipmentId = Number(id);

    const openLabelPreview = (
        checkItemId: number,
        packageIndex: number,
        itemCode: string | null,
        boxLabel: string,
        initialTab: 'preview' | 'zpl' = 'preview',
    ) => {
        setLabelPreview({
            checkItemId,
            packageIndex,
            title: `${itemCode ?? 'Item'} · caixa ${boxLabel}`,
            initialTab,
        });
    };

    const { data, isLoading, error } = useQuery({
        queryKey: ['shipment-conference', id, invoice, container],
        queryFn: async () => {
            const params = new URLSearchParams();
            if (invoice) params.set('invoice', invoice);
            if (container) params.set('container', container);
            const qs = params.toString();
            return (
                await api.get<State>(
                    `/shipments/${id}/conference${qs ? `?${qs}` : ''}`,
                )
            ).data;
        },
    });

    const containersForInvoice = useMemo(() => {
        if (!data) return [];
        if (!invoice) return data.summary.containers;
        return data.summary.containers.filter((c) => c.invoice_number === invoice);
    }, [data, invoice]);

    const filteredRows = useMemo(() => {
        if (!data) return [];
        if (!colorFilter) return data.package_rows;
        return data.package_rows.filter((r) => r.color_key === colorFilter);
    }, [data, colorFilter]);

    const hasAnyWeightTowers = useMemo(
        () => filteredRows.some((r) => r.weight_tower_qty != null),
        [filteredRows],
    );

    const invalidate = () => {
        queryClient.invalidateQueries({ queryKey: ['shipment-conference', id] });
    };

    if (isLoading) return <LoadingState />;
    if (error) return <p className="p-6 text-sm text-red-600">Erro ao carregar conferência.</p>;
    if (!data) return null;

    const onInvoiceChange = (inv: string) => {
        setInvoice(inv);
        setColorFilter('');
        const first = data.summary.containers.find((c) => !inv || c.invoice_number === inv);
        setContainer(first?.container_number ?? '');
    };

    return (
        <div className="space-y-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <nav className="text-xs text-gray-500">
                        <Link to="/shipments" className="hover:underline">
                            Embarques
                        </Link>
                        <span className="mx-1">&gt;</span>
                        <Link to={`/shipments/${id}`} className="hover:underline">
                            {data.shipment.order_number ?? `#${id}`}
                        </Link>
                        <span className="mx-1">&gt;</span>
                        <span>Conferência CD</span>
                    </nav>
                    <h1 className="mt-1 text-2xl font-bold text-gray-800">Romaneio de conferência</h1>
                </div>
                <div className="flex flex-wrap items-end gap-2">
                    <a
                        href={data.urls.conference_v1}
                        className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                        <PiArrowSquareOutBold /> v1
                    </a>
                    <Link
                        to={`/shipments/${id}/check`}
                        className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                        <PiClipboardTextBold /> Conferência linha a linha
                    </Link>
                    <Link
                        to={`/shipments/${id}/pi-check`}
                        className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                        Validar PI × PO
                    </Link>
                </div>
            </div>

            {!data.summary.has_checks ? (
                <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
                    <p>
                        Nenhuma planilha de conferência importada. Envie o arquivo <strong>Details</strong> do
                        fornecedor (uma aba por container, ex.:{' '}
                        <code className="text-xs">SP-0119_Details.xlsx</code>).
                    </p>
                    <div className="mt-3">
                        <ConferenceSheetImportButton
                            shipmentId={shipmentId}
                            onSuccess={invalidate}
                        />
                    </div>
                </div>
            ) : (
                <>
                    <div className="flex flex-wrap items-end gap-3 rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <label className="block min-w-[140px] text-xs text-gray-500">
                            Invoice
                            <select
                                value={invoice}
                                onChange={(e) => onInvoiceChange(e.target.value)}
                                className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-2 text-sm"
                            >
                                <option value="">Todas</option>
                                {data.summary.invoices.map((inv) => (
                                    <option key={inv} value={inv}>
                                        {inv}
                                    </option>
                                ))}
                            </select>
                        </label>
                        <label className="block min-w-[200px] text-xs text-gray-500">
                            Container
                            <select
                                value={container}
                                onChange={(e) => {
                                    setContainer(e.target.value);
                                    setColorFilter('');
                                }}
                                className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-2 text-sm"
                            >
                                {containersForInvoice.map((c) => (
                                    <option key={c.id} value={c.container_number}>
                                        {c.container_number}
                                        {c.invoice_number ? ` (${c.invoice_number})` : ''}
                                    </option>
                                ))}
                            </select>
                        </label>
                        {data.color_options.length > 0 ? (
                            <label className="block min-w-[220px] flex-1 text-xs text-gray-500">
                                Cor (combinação)
                                <select
                                    value={colorFilter}
                                    onChange={(e) => setColorFilter(e.target.value)}
                                    className="mt-1 w-full rounded-lg border border-gray-300 px-2 py-2 text-sm"
                                >
                                    <option value="">Todas as cores</option>
                                    {data.color_options.map((c) => (
                                        <option key={c.key} value={c.key}>
                                            {c.label}
                                        </option>
                                    ))}
                                </select>
                            </label>
                        ) : null}
                        <div className="flex flex-wrap gap-2 pb-0.5">
                            {data.urls.pdf_container ? (
                                <a
                                    href={data.urls.pdf_container}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-white hover:bg-primary-dark"
                                >
                                    <PiFilePdfBold /> PDF container
                                </a>
                            ) : null}
                            {data.urls.pdf_orders ? (
                                <a
                                    href={data.urls.pdf_orders}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                >
                                    <PiFilePdfBold /> PDF pedido
                                </a>
                            ) : null}
                            {data.urls.labels_bulk_pdf ? (
                                <a
                                    href={data.urls.labels_bulk_pdf}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-600 bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700"
                                >
                                    <PiFilePdfBold /> PDF etiquetas (10×10 cm)
                                </a>
                            ) : null}
                            {data.urls.labels_bulk_csv ? (
                                <a
                                    href={data.urls.labels_bulk_csv}
                                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                                >
                                    <PiTagBold /> CSV (Label Designer)
                                </a>
                            ) : null}
                            {(data.urls.labels_bulk_zpl ?? data.urls.labels_bulk_tprt) ? (
                                <a
                                    href={data.urls.labels_bulk_zpl ?? data.urls.labels_bulk_tprt ?? '#'}
                                    className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-600 hover:bg-gray-50"
                                >
                                    <PiTagBold /> ZPL (avançado)
                                </a>
                            ) : null}
                        </div>
                    </div>

                    {data.totals ? (
                        <div className="rounded-xl border border-gray-200 bg-white shadow-sm">
                            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-gray-100 px-4 py-3">
                                <div className="flex items-center gap-2 text-sm text-gray-600">
                                    <PiCubeBold className="size-5 text-primary" />
                                    <span>
                                        <strong>{data.selection.container_number}</strong>
                                        {data.selection.invoice_number
                                            ? ` · Invoice ${data.selection.invoice_number}`
                                            : ''}
                                        {' — '}
                                        {data.totals.lines} itens · {data.totals.package_rows} caixas
                                        {data.totals.validated > 0
                                            ? ` · ${data.totals.validated} validadas`
                                            : ''}
                                    </span>
                                </div>
                                <p className="max-w-xl text-[10px] text-gray-500">
                                    Uma linha por caixa; marque cada pacote conferido no CD.
                                    {!hasAnyWeightTowers ? (
                                        <span className="mt-1 block text-amber-700">
                                            Torres de peso: preencha o campo &quot;Torre de Peso por
                                            Equipamento&quot; no cadastro de Produtos (ou reimporte a
                                            Details após atualizar).
                                        </span>
                                    ) : null}
                                </p>
                            </div>
                            <div className="overflow-x-auto">
                                <table className="min-w-full text-xs">
                                    <thead className="bg-gray-50 text-gray-600">
                                        <tr>
                                            <th className="px-3 py-2 text-left">Cliente</th>
                                            <th className="px-3 py-2 text-left">Item</th>
                                            <th className="px-3 py-2 text-right">Qtd</th>
                                            <th className="px-3 py-2 text-left">Produto</th>
                                            <th className="px-3 py-2 text-left">Estrutura</th>
                                            <th className="px-3 py-2 text-left">Estofado</th>
                                            <th className="px-3 py-2 text-left">Costura</th>
                                            <th className="px-3 py-2 text-left">Braço</th>
                                            <th className="px-3 py-2 text-center">Caixa</th>
                                            <th className="px-3 py-2 text-right">Torres</th>
                                            <th className="px-3 py-2 text-right">Peso</th>
                                            <th className="px-3 py-2 text-center">Etiqueta</th>
                                            <th className="px-3 py-2 text-center">Conferido</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100">
                                        {filteredRows.map((row) => (
                                            <tr
                                                key={row.row_key}
                                                className={
                                                    row.validated ? 'bg-emerald-50/40' : undefined
                                                }
                                            >
                                                <td className="px-3 py-2">{row.client ?? '—'}</td>
                                                <td className="px-3 py-2 font-mono">
                                                    {row.item_code ?? '—'}
                                                </td>
                                                <td className="px-3 py-2 text-right tabular-nums">
                                                    {row.qty_total}
                                                </td>
                                                <td className="max-w-[200px] px-3 py-2">
                                                    <span className="line-clamp-2">
                                                        {row.product_name ?? '—'}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2 text-[11px]">
                                                    {row.frame || '—'}
                                                </td>
                                                <td className="px-3 py-2 text-[11px]">
                                                    {row.upholstery || '—'}
                                                </td>
                                                <td className="px-3 py-2 text-[11px]">
                                                    {row.stitches || '—'}
                                                </td>
                                                <td className="px-3 py-2 text-[11px]">
                                                    {row.arm && row.arm !== '-' ? row.arm : '—'}
                                                </td>
                                                <td className="px-3 py-2 text-center font-bold tabular-nums text-gray-900">
                                                    {row.box_label}
                                                    <span className="font-normal text-gray-400">
                                                        /{row.package_total}
                                                    </span>
                                                </td>
                                                <td className="px-3 py-2 text-right tabular-nums">
                                                    {row.weight_tower_qty != null
                                                        ? row.weight_tower_qty
                                                        : '—'}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                                                    {row.net_weight_kg != null
                                                        ? `${row.net_weight_kg.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg`
                                                        : '—'}
                                                </td>
                                                <td className="whitespace-nowrap px-3 py-2 text-center">
                                                    <div className="flex flex-col items-center gap-1">
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                openLabelPreview(
                                                                    row.check_item_id,
                                                                    row.package_index,
                                                                    row.item_code,
                                                                    row.box_label,
                                                                )
                                                            }
                                                            className="inline-flex items-center rounded-md border border-primary/30 bg-primary/5 px-2 py-0.5 text-[11px] font-medium text-primary hover:bg-primary/10"
                                                        >
                                                            Preview
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={() =>
                                                                openLabelPreview(
                                                                    row.check_item_id,
                                                                    row.package_index,
                                                                    row.item_code,
                                                                    row.box_label,
                                                                    'zpl',
                                                                )
                                                            }
                                                            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-2 py-0.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
                                                        >
                                                            ZPL
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="px-3 py-2 text-center">
                                                    <ValidationToggle
                                                        row={row}
                                                        shipmentId={shipmentId}
                                                        onUpdated={invalidate}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    ) : null}

                    {data.labels.length > 0 ? (
                        <div
                            id="labels"
                            className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
                        >
                            <h2 className="mb-1 flex items-center gap-2 text-sm font-semibold text-gray-800">
                                <PiTagBold className="text-emerald-600" /> Etiquetas por caixa
                            </h2>
                            <p className="mb-4 text-xs text-gray-500">
                                {data.label_totals?.labels ?? data.labels.length} etiquetas — Picking
                                List 10×10 cm (baixe o ZPL de novo antes de imprimir)
                            </p>
                            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
                                {data.labels.map((label) => (
                                    <article
                                        key={label.id}
                                        className="overflow-hidden rounded-xl border border-gray-200 bg-gradient-to-b from-white to-gray-50 shadow-sm"
                                    >
                                        <div className="bg-gradient-to-r from-orange-600 to-orange-500 px-3 py-2 text-white">
                                            <div className="flex items-start justify-between gap-2">
                                                <div className="min-w-0">
                                                    <img
                                                        src="/img/design/Logo_Buckler_OffWhite.png"
                                                        alt="Buckler"
                                                        className="h-6 w-auto max-w-[120px] object-contain object-left"
                                                    />
                                                    <p className="mt-0.5 text-[10px] font-medium uppercase tracking-wide opacity-90">
                                                        Picking List
                                                    </p>
                                                </div>
                                                <div className="shrink-0 text-right">
                                                    <p className="text-[9px] font-medium uppercase tracking-wide opacity-85">
                                                        Container
                                                    </p>
                                                    <p
                                                        className="max-w-[120px] truncate font-mono text-xs font-bold"
                                                        title={label.container_number ?? undefined}
                                                    >
                                                        {label.container_number ?? '—'}
                                                    </p>
                                                    <p className="mt-0.5 text-[10px] opacity-90">
                                                        Invoice: {label.invoice_number ?? '—'}
                                                    </p>
                                                </div>
                                            </div>
                                            <p className="mt-2 truncate text-base font-bold">
                                                {label.client_label}
                                            </p>
                                            <p className="text-[11px] opacity-90">
                                                Ped.: {label.order_ref}
                                            </p>
                                            <p className="text-[10px] opacity-85">
                                                Conferido: {label.conferido_por ?? '—'} · Pedido:{' '}
                                                {label.order_date_label ?? '—'} · Entrada:{' '}
                                                {label.entry_date_label ?? '—'}
                                            </p>
                                        </div>
                                        <div className="border-b border-gray-100 px-3 py-2">
                                            <p className="text-[9px] font-semibold uppercase tracking-wide text-gray-500">
                                                Case No
                                            </p>
                                            <p className="font-mono text-2xl font-bold leading-tight text-gray-900">
                                                {label.case_no ??
                                                    label.box_label ??
                                                    label.package_index}
                                            </p>
                                            <span className="mt-1 inline-block rounded-md bg-gray-900 px-2 py-0.5 text-xs font-bold text-white">
                                                Caixa {label.box_label ?? label.package_index}/
                                                {label.package_total}
                                            </span>
                                        </div>
                                        <div className="px-3 py-2">
                                            <p className="font-mono text-sm font-bold text-gray-900">
                                                {label.item_code ?? '—'}
                                            </p>
                                            <p className="line-clamp-2 text-[11px] text-gray-600">
                                                {label.product_name ?? '—'}
                                            </p>
                                        </div>
                                        <div className="grid grid-cols-2 gap-1.5 px-3 pb-1 text-[10px]">
                                            <div className="rounded bg-white px-2 py-1 ring-1 ring-gray-100">
                                                <span className="text-gray-400">Estr.</span>{' '}
                                                {label.frame_color || '—'}
                                            </div>
                                            <div className="rounded bg-white px-2 py-1 ring-1 ring-gray-100">
                                                <span className="text-gray-400">Est.</span>{' '}
                                                {label.upholstery_color || '—'}
                                            </div>
                                            <div className="rounded bg-white px-2 py-1 ring-1 ring-gray-100">
                                                <span className="text-gray-400">Cos.</span>{' '}
                                                {label.stitches_color || '—'}
                                            </div>
                                            <div className="rounded bg-white px-2 py-1 ring-1 ring-gray-100">
                                                <span className="text-gray-400">Braço</span>{' '}
                                                {label.arm_color || '—'}
                                            </div>
                                        </div>
                                        <div className="mx-3 mb-2 grid grid-cols-3 gap-1 rounded-lg bg-orange-50 px-2 py-1.5 text-[10px] ring-1 ring-orange-100">
                                            <div>
                                                <span className="text-orange-800/70">Cubagem</span>
                                                <p className="font-semibold text-orange-950">
                                                    {label.box_cbm_m3 != null
                                                        ? `${label.box_cbm_m3.toLocaleString('pt-BR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })} m³`
                                                        : '—'}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-orange-800/70">Torres</span>
                                                <p className="font-semibold text-orange-950">
                                                    {label.weight_tower_qty != null &&
                                                    label.weight_tower_qty > 0
                                                        ? label.weight_tower_qty
                                                        : '—'}
                                                </p>
                                            </div>
                                            <div>
                                                <span className="text-orange-800/70">Peso</span>
                                                <p className="font-semibold text-orange-950">
                                                    {(label.box_weight_kg ?? label.net_weight_kg) != null
                                                        ? `${(label.box_weight_kg ?? label.net_weight_kg)!.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kg`
                                                        : '—'}
                                                </p>
                                            </div>
                                        </div>
                                        <div className="flex flex-wrap gap-2 border-t border-gray-100 px-3 py-2 text-xs">
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    openLabelPreview(
                                                        label.check_item_id,
                                                        label.package_index,
                                                        label.item_code,
                                                        String(label.box_label ?? label.package_index),
                                                    )
                                                }
                                                className="font-medium text-primary hover:underline"
                                            >
                                                Preview
                                            </button>
                                            {label.pdf_url ? (
                                                <a
                                                    href={label.pdf_url}
                                                    className="font-medium text-emerald-700 hover:underline"
                                                >
                                                    PDF
                                                </a>
                                            ) : null}
                                            <button
                                                type="button"
                                                onClick={() =>
                                                    openLabelPreview(
                                                        label.check_item_id,
                                                        label.package_index,
                                                        label.item_code,
                                                        String(label.box_label ?? label.package_index),
                                                        'zpl',
                                                    )
                                                }
                                                className="text-gray-500 hover:underline"
                                            >
                                                ZPL
                                            </button>
                                        </div>
                                    </article>
                                ))}
                            </div>
                        </div>
                    ) : null}

                    <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
                        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-gray-800">
                            <PiPackageBold /> Por pedido
                        </h2>
                        <div className="overflow-x-auto">
                            <table className="min-w-full text-xs">
                                <thead className="bg-gray-50 text-gray-600">
                                    <tr>
                                        <th className="px-2 py-2 text-left">Cliente</th>
                                        <th className="px-2 py-2 text-right">Qtd</th>
                                        <th className="px-2 py-2 text-right">Packages</th>
                                        <th className="px-2 py-2 text-left">Status</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {data.orders.map((o) => (
                                        <tr key={o.order_key}>
                                            <td className="px-2 py-1.5">{o.client_label}</td>
                                            <td className="px-2 py-1.5 text-right tabular-nums">
                                                {o.qty_total}
                                            </td>
                                            <td className="px-2 py-1.5 text-right tabular-nums">
                                                {o.packages_total}
                                            </td>
                                            <td className="px-2 py-1.5">
                                                {o.fulfillment_status ===
                                                'partial_multi_invoice' ? (
                                                    <span className="font-medium text-amber-700">
                                                        Parcial
                                                    </span>
                                                ) : (
                                                    <span className="text-emerald-700">OK</span>
                                                )}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </>
            )}

            <ConferenceLabelPreviewModal
                shipmentId={shipmentId}
                target={labelPreview}
                onClose={() => setLabelPreview(null)}
            />
        </div>
    );
}
